import type { IconName } from '@/ui/icons';

/**
 * The kind of absence a type is (absence_types.category, migration 20260921210000). The fixed
 * list the product reads for colours, filters, statistics and balances; the type's own name is
 * the company's. Order is the order a manager meets them.
 *
 * `short` is the word a narrow calendar block can carry whole (#272 B-S04).
 *
 * `tone` is a palette name from tokens.css (the -f fill, -l line and -i ink triple), so a block,
 * a chip and a legend dot of one category are the same colour on every screen and in dark mode.
 * The type's own color_code is the company's and is not used here: two companies' Ferie would
 * otherwise look different, and a company's twelve colours would not read as four kinds.
 */
export const CATEGORIES = [
  { key: 'ferie', label: 'Ferie', short: 'Ferie', tone: 'ok', icon: 'sun' },
  { key: 'egenmelding', label: 'Egenmelding', short: 'Egenm.', tone: 'warn', icon: 'document' },
  { key: 'sykemelding', label: 'Sykemelding', short: 'Sykm.', tone: 'violet', icon: 'medical' },
  { key: 'syk_barn', label: 'Syk barn', short: 'Syk b.', tone: 'magenta', icon: 'heart' },
  { key: 'permisjon', label: 'Permisjon', short: 'Perm.', tone: 'blue', icon: 'briefcase' },
  { key: 'annet', label: 'Annet', short: 'Annet', tone: 'slate', icon: 'calendarOff' },
] as const satisfies readonly { key: string; label: string; short: string; tone: string; icon: IconName }[];
export type Category = (typeof CATEGORIES)[number]['key'];
export const categoryLabel = (c: string | null | undefined) =>
  CATEGORIES.find((x) => x.key === c)?.label ?? 'Annet';

/** The category a type or an absence's type is in; a type without one (an older server) is Annet. */
export const catOf = (t: { category?: string | null } | null | undefined): Category =>
  (CATEGORIES.find((x) => x.key === t?.category)?.key ?? 'annet') as Category;
export const catInfo = (c: Category) => CATEGORIES.find((x) => x.key === c) ?? CATEGORIES[5];

/** Sick leave in the official measure (SSB/NAV): egenmelding and sykemelding. Syk barn is not. */
export const isSickLeave = (c: Category) => c === 'egenmelding' || c === 'sykemelding';
